<template>
  <div class="requestor-portal-flow">
    <section v-for="section in sections" :key="section.id" class="requestor-portal-form-section border mb-4">
      <header class="requestor-portal-form-section-header mb-3">
        <div class="requestor-portal-label text-uppercase mb-1">{{ section.name }}</div>
        <div v-if="section.description" class="requestor-portal-copy">{{ section.description }}</div>
      </header>

      <div v-for="question in section.questions || []" :key="questionKey(question)" class="mb-3" :data-question-key="questionKey(question)">
        <label class="requestor-portal-label text-uppercase mb-2">
          {{ question.question_text }}
          <span v-if="isRequired(question)" class="text-danger fw-bold ms-1">*</span>
        </label>

        <select
          v-if="isSelect(question) && !isMultiSelect(question)"
          :value="answerValue(question)"
          class="form-select requestor-portal-date w-100"
          :class="{ 'is-invalid': hasError(question) }"
          :disabled="disabled"
          @change="emitAnswer(question, $event.target.value)"
        >
          <option value="">Select an option</option>
          <option v-for="option in choices(question)" :key="optionKey(option)" :value="optionValue(option)">
            {{ optionValue(option) }}
          </option>
        </select>

        <select
          v-else-if="isMultiSelect(question)"
          :value="multiAnswerValue(question)"
          class="form-select requestor-portal-date w-100"
          :class="{ 'is-invalid': hasError(question) }"
          :disabled="disabled"
          multiple
          @change="emitAnswer(question, Array.from($event.target.selectedOptions || []).map((option) => option.value))"
        >
          <option v-for="option in choices(question)" :key="optionKey(option)" :value="optionValue(option)">
            {{ optionValue(option) }}
          </option>
        </select>

        <div v-else-if="isCheckbox(question)" :class="{ 'is-invalid': hasError(question) }">
          <div v-for="option in choices(question)" :key="optionKey(option)" class="form-check">
            <input
              :id="`${idPrefix}-${questionKey(question)}-${optionKey(option)}`"
              class="form-check-input"
              type="checkbox"
              :value="optionValue(option)"
              :checked="multiAnswerValue(question).includes(optionValue(option))"
              :disabled="disabled"
              @change="updateCheckbox(question, optionValue(option), $event.target.checked)"
            >
            <label class="form-check-label requestor-portal-copy" :for="`${idPrefix}-${questionKey(question)}-${optionKey(option)}`">
              {{ optionValue(option) }}
            </label>
          </div>
        </div>

        <div v-else-if="isRadio(question)" :class="{ 'is-invalid': hasError(question) }">
          <div v-for="option in choices(question)" :key="optionKey(option)" class="form-check">
            <input
              :id="`${idPrefix}-${questionKey(question)}-${optionKey(option)}`"
              class="form-check-input"
              type="radio"
              :name="`${idPrefix}-${questionKey(question)}`"
              :value="optionValue(option)"
              :checked="answerValue(question) === optionValue(option)"
              :disabled="disabled"
              @change="emitAnswer(question, $event.target.value)"
            >
            <label class="form-check-label requestor-portal-copy" :for="`${idPrefix}-${questionKey(question)}-${optionKey(option)}`">
              {{ optionValue(option) }}
            </label>
          </div>
        </div>

        <textarea
          v-else-if="isLongText(question)"
          :value="answerValue(question)"
          class="form-control requestor-portal-textarea"
          :class="{ 'is-invalid': hasError(question) }"
          rows="4"
          :placeholder="question.question_description || ''"
          :disabled="disabled"
          @input="emitAnswer(question, $event.target.value)"
        ></textarea>

        <input
          v-else-if="isFile(question)"
          type="file"
          :accept="acceptedFileTypes(question)"
          class="form-control requestor-portal-date w-100"
          :class="{ 'is-invalid': hasError(question) }"
          :disabled="disabled"
          @change="emitAnswer(question, Array.from($event.target.files || [])[0] || null)"
        >

        <input
          v-else
          :value="answerValue(question)"
          :type="inputType(question)"
          :step="inputStep(question)"
          class="form-control requestor-portal-date w-100"
          :class="{ 'is-invalid': hasError(question) }"
          :placeholder="question.question_description || ''"
          :disabled="disabled"
          @input="emitAnswer(question, $event.target.value)"
        >

        <div v-if="hasError(question)" class="small text-danger mt-2 fw-semibold">
          {{ errorMessage(question) }}
        </div>
      </div>
    </section>
  </div>
</template>

<script>
function text(value) {
  return String(value || '').trim();
}

function questionType(question = {}) {
  return text(question?.question_type?.question_type || question?.question_type?.type || question?.question_type).toLowerCase();
}

export default {
  name: 'ConfiguredCaseForm',
  props: {
    sections: { type: Array, default: () => [] },
    answerValues: { type: Array, default: () => [] },
    validationErrors: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    idPrefix: { type: String, default: 'configured-case-form' },
  },
  emits: ['answer-change'],
  methods: {
    questionKey(question = {}) {
      return String(question?.form_question_key || question?.section_question_id || question?.question_id || question?.id || question?.question_text || 'question');
    },
    answerValue(question = {}) {
      return this.answerValues.find((record) => record.key === this.questionKey(question))?.value || '';
    },
    multiAnswerValue(question = {}) {
      const value = this.answerValues.find((record) => record.key === this.questionKey(question))?.value;
      return Array.isArray(value) ? value : [];
    },
    emitAnswer(question, value) {
      this.$emit('answer-change', { question, value });
    },
    updateCheckbox(question, option, checked) {
      const current = this.multiAnswerValue(question);
      const value = checked ? [...new Set([...current, option])] : current.filter((item) => item !== option);
      this.emitAnswer(question, value);
    },
    choices(question = {}) {
      return question?.multi_select_choices || question?.options || [];
    },
    optionValue(option) {
      return option?.choice || option?.label || option?.value || option;
    },
    optionKey(option) {
      return String(option?.id || this.optionValue(option));
    },
    isRequired(question = {}) {
      return Boolean(question?.is_required || question?.is_mandatory);
    },
    isSelect(question) { return questionType(question).includes('select'); },
    isMultiSelect(question) { return questionType(question).replace(/\s+/g, '_').includes('multi_select'); },
    isCheckbox(question) { return questionType(question).includes('checkbox'); },
    isRadio(question) { return questionType(question).includes('radio'); },
    isLongText(question) { return ['long', 'text_area', 'textarea'].some((type) => questionType(question).includes(type)); },
    isFile(question) { return ['file', 'upload', 'attachment', 'document', 'facial_verification'].some((type) => questionType(question).includes(type)); },
    acceptedFileTypes(question) { return questionType(question).includes('facial_verification') ? 'image/*' : undefined; },
    inputType(question) {
      const type = questionType(question);
      if (type.includes('datetime')) return 'datetime-local';
      if (type.includes('date')) return 'date';
      if (type.includes('time')) return 'time';
      if (['number', 'integer', 'float', 'decimal', 'currency'].some((value) => type.includes(value))) return 'number';
      if (type.includes('email')) return 'email';
      if (type.includes('phone') || type.includes('tel')) return 'tel';
      if (type.includes('url')) return 'url';
      return 'text';
    },
    inputStep(question) {
      const type = questionType(question);
      return ['number', 'float', 'decimal', 'currency'].some((value) => type.includes(value)) ? 'any' : undefined;
    },
    errorMessage(question) {
      return this.validationErrors.find((error) => error.key === this.questionKey(question))?.message || '';
    },
    hasError(question) { return Boolean(this.errorMessage(question)); },
  },
};
</script>
